import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ReactNode } from "react";

export interface CriterionHelpDialogTableSectionProps {
  /** タイトル */
  title: string;
  /** テーブルデータ */
  table: {
    head: ReactNode[];
    body: {
      label: ReactNode;
      data: ReactNode[][];
    }[];
  };
  /** 備考 */
  remarks?: string[];
}

export function CriterionHelpDialogTableSection({
  title,
  table: { head, body },
  remarks,
}: CriterionHelpDialogTableSectionProps) {
  return (
    <Stack gap={4}>
      <Typography variant="subtitle1">{title}</Typography>
      <Stack gap={2}>
        <TableContainer
          sx={{
            borderRadius: 0,
            "& .MuiTableCell-root": {
              borderBottom: "none",
            },
            "& .MuiTable-root": {
              border: "1px solid #e0e0e0",
              tableLayout: "fixed",
              "& .MuiTableCell-head": {
                px: 0,
                py: 4,
                "&>*": {
                  px: 4,
                  fontWeight: 500,
                  color: "#3d3d3d",
                },
                "&:not(:first-of-type)>*": {
                  borderLeft: "1px solid #e0e0e0",
                },
                "&:last-of-type": {
                  width: "80px",
                  "&>*": {
                    textAlign: "center",
                  },
                },
              },
              "& .MuiTableCell-body": {
                "&>*": {
                  color: "#3d3d3d",
                },
                "&:has(*)": {
                  borderTop: "1px solid #e0e0e0",
                },
                "&:last-of-type": {
                  borderLeft: "1px solid #e0e0e0",
                  textAlign: "center",
                  "& img": {
                    display: "block",
                    margin: "auto",
                  },
                  "&>p": {
                    fontWeight: 500,
                  },
                },
                "&:has(+:not(:first-of-type):not(:last-of-type))": {
                  borderRight: "1px dashed #e0e0e0",
                  verticalAlign: "top",
                },
              },
            },
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                {head.map((v, i) => (
                  <TableCell key={i}>
                    {typeof v === "string" ? (
                      <Typography variant="subtitle2">{v}</Typography>
                    ) : (
                      v
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {body.map((x, i) =>
                x.data.map((d, j) => (
                  <TableRow key={i}>
                    {j === 0 && (
                      <TableCell key={`${i}-${j}`} rowSpan={x.data.length}>
                        {typeof x.label === "string" ? (
                          <Typography variant="body2">{x.label}</Typography>
                        ) : (
                          x.label
                        )}
                      </TableCell>
                    )}
                    {d.map((c, k) => (
                      <TableCell key={`${i}-${j}-${k}`}>
                        {typeof c === "string" ? (
                          <Typography variant="body2">{c}</Typography>
                        ) : (
                          (c ?? <></>)
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {remarks && (
          <Stack>
            {remarks.map((r, i) => (
              <Typography
                key={i}
                sx={{ fontSize: "14px", fontWeight: 400, color: "#707070" }}
              >
                {r}
              </Typography>
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

export default function CriterionHelpDialogTableSections({
  data,
}: {
  data: CriterionHelpDialogTableSectionProps[];
}) {
  return (
    <Stack gap={6}>
      {data.map((d, i) => (
        <CriterionHelpDialogTableSection {...d} key={i} />
      ))}
    </Stack>
  );
}
